import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode; 
  allowedRoles?: string[];
}

function normalizeRole(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const str = String(value).trim().toLowerCase();
  if (!str) return null;
  // Map common variants to canonical keys
  if (str === 'professional' || str === 'professionnel') return 'professional';
  if (str === 'partner' || str === 'partenaire') return 'partner';
  if (str === 'admin' ) return 'admin';
  if (str === 'client'  ) return 'client';
  if (str === 'doctortester' || str === 'doctor_tester' || str === 'doctor-tester') return 'doctortester';
  return str;
}

function getCurrentRole(): string | null {
  // Primary: explicit Role entry set on sign-in
  const storedRole = localStorage.getItem('Role');
  if (storedRole) {
    try {
      return normalizeRole(JSON.parse(storedRole));
    } catch {
      return normalizeRole(storedRole);
    }
  }

  // Fallback: userInfo payload may contain a role
  const userInfo = localStorage.getItem('userInfo');
  if (userInfo) {
    try {
      const parsed = JSON.parse(userInfo);
      const candidate = parsed?.role ?? parsed?.user?.role;
      const normalized = normalizeRole(candidate);
      if (normalized) return normalized;
    } catch {
      // ignore
    }
  }

  // Last resort: user from admin login
  const user = localStorage.getItem('user');
  if (user) {
    try {
      const parsed = JSON.parse(user);
      const normalized = normalizeRole(parsed?.role);
      if (normalized) return normalized;
    } catch {
      // ignore
    }
  }

  return null;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isTokenExpired, validateAndRefreshToken } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(false);
  const [checkDone, setCheckDone] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  const role = useMemo(() => getCurrentRole(), [location.pathname, isAuthenticated]);
  const wantsAdmin = (allowedRoles || []).some(r => normalizeRole(r) === 'admin');
  const wantsDoctorTester = (allowedRoles || []).some(r => normalizeRole(r) === 'doctortester');

  if (allowedRoles && allowedRoles.length > 0) {
    const normalizedAllowed = allowedRoles
      .map(normalizeRole)
      .filter((r): r is string => !!r);

    if (role && normalizedAllowed.includes(role)) {
      // If access token is expired on first render (e.g., after page refresh), attempt a refresh before rendering children
      if (!checkDone && isTokenExpired()) {
        if (!isChecking) {
          setIsChecking(true);
          (async () => {
            try {
              await validateAndRefreshToken();
              setRefreshFailed(false);
            } catch {
              setRefreshFailed(true);
            } finally {
              setIsChecking(false);
              setCheckDone(true);
            }
          })();
        }
        // While checking, do not render children to avoid unauthorized calls
        return null;
      }

      if (refreshFailed) {
        return <Navigate to={wantsAdmin ? '/login' : (wantsDoctorTester ? '/signInTester' : '/signIn')} state={{ from: location }} replace />;
      }

      return <>{children}</>;
    }

    // Not authorized → send to appropriate login page
    return <Navigate to={wantsAdmin ? '/login' : (wantsDoctorTester ? '/signInTester' : '/signIn')} state={{ from: location }} replace />;
  }

  // Backward compatibility: plain auth check (used for legacy admin protection)
  if (!isAuthenticated) {
    // Redirect to login page with the current location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;